import { NARRATION_BOUNDS } from '../config/narration';
import type { NarrationOptions, NarrationProfile, NarrationService } from '../../sdk/narration';

/**
 * Browser text-to-speech narration (MVP notes §18).
 *
 * The engine is platform-owned and replaceable: a module supplies a `NarrationProfile` and
 * nothing else. Recorded or generated character clips can land later behind this same
 * interface without touching a single game file.
 *
 * Two behaviours matter for a five-year-old: speech never overlaps, and `stop()` really stops.
 * Requests are therefore queued and played one at a time, and every pending promise settles
 * when narration is cancelled so callers can never hang waiting for a voice that was silenced.
 */

export interface SpeechSynthesisLike {
  speak(utterance: SpeechSynthesisUtterance): void;
  cancel(): void;
}

export type UtteranceFactory = (text: string) => SpeechSynthesisUtterance;

export interface BrowserTtsNarrationServiceOptions {
  /** Defaults to `window.speechSynthesis` when available. */
  synthesis?: SpeechSynthesisLike | null;
  /** Defaults to `new SpeechSynthesisUtterance(text)`; injected in tests. */
  utteranceFactory?: UtteranceFactory | null;
  profile?: NarrationProfile;
  enabled?: boolean;
  /** 0..1 */
  volume?: number;
}

interface QueuedNarration {
  text: string;
  options: NarrationOptions | undefined;
  settle: () => void;
}

function defaultSynthesis(): SpeechSynthesisLike | null {
  if (typeof globalThis === 'undefined') return null;
  const candidate = (globalThis as { speechSynthesis?: SpeechSynthesisLike }).speechSynthesis;
  return candidate ?? null;
}

function defaultUtteranceFactory(): UtteranceFactory | null {
  const Utterance = (
    globalThis as {
      SpeechSynthesisUtterance?: new (text: string) => SpeechSynthesisUtterance;
    }
  ).SpeechSynthesisUtterance;
  if (Utterance === undefined) return null;
  return (text: string) => new Utterance(text);
}

/**
 * How long the rest of a line may take, once the engine has actually started saying it (S5-6).
 *
 * Measured from the start rather than from `speak()`, re-armed against what is **left** of the text
 * at every word boundary the engine reports, and divided by the rate the line is being spoken at, so
 * a deliberately slow voice profile is given proportionally longer. See `NARRATION_BOUNDS` for why
 * the per-character figure is derived from the slowest delivery anybody hears rather than the
 * ordinary one: this may only ever fire for a line nobody is saying any more.
 */
function remainingSpeechMs(text: string, spokenThrough: number, rate: number): number {
  const safeRate = Number.isFinite(rate) && rate > 0 ? rate : 1;
  const remaining = Math.max(0, text.length - Math.max(0, spokenThrough));
  const spoken = remaining * NARRATION_BOUNDS.utteranceMsPerCharacter;
  return (NARRATION_BOUNDS.utteranceProgressGraceMs + spoken) / safeRate;
}

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

export class BrowserTtsNarrationService implements NarrationService {
  readonly #synthesis: SpeechSynthesisLike | null;
  readonly #createUtterance: UtteranceFactory | null;
  readonly #queue: QueuedNarration[] = [];

  #profile: NarrationProfile | undefined;
  #voiceProfileId: string | undefined;
  #enabled: boolean;
  #volume: number;
  #speaking = false;
  /** Settles the utterance currently in flight, so `stop()` never leaves a caller hanging. */
  #finishActive: (() => void) | null = null;
  #last: { text: string; options: NarrationOptions | undefined } | null = null;

  constructor(options: BrowserTtsNarrationServiceOptions = {}) {
    this.#synthesis = options.synthesis === undefined ? defaultSynthesis() : options.synthesis;
    this.#createUtterance =
      options.utteranceFactory === undefined ? defaultUtteranceFactory() : options.utteranceFactory;
    this.#profile = options.profile;
    this.#voiceProfileId = options.profile?.defaultVoiceProfileId;
    this.#enabled = options.enabled ?? true;
    this.#volume = clampVolume(options.volume ?? 1);
  }

  /** False in environments with no speech synthesis; every call then becomes a safe no-op. */
  get available(): boolean {
    return this.#synthesis !== null && this.#createUtterance !== null;
  }

  get enabled(): boolean {
    return this.#enabled;
  }

  get volume(): number {
    return this.#volume;
  }

  get voiceProfileId(): string | undefined {
    return this.#voiceProfileId;
  }

  setProfile(profile: NarrationProfile): void {
    this.#profile = profile;
    this.#voiceProfileId = profile.defaultVoiceProfileId;
  }

  setEnabled(enabled: boolean): void {
    this.#enabled = enabled;
    if (!enabled) this.stop();
  }

  setVolume(volume: number): void {
    this.#volume = clampVolume(volume);
  }

  setVoiceProfile(profile: string): void {
    this.#voiceProfileId = profile;
  }

  async speak(text: string, options?: NarrationOptions): Promise<void> {
    this.#last = { text, options };

    if (!this.#enabled || !this.available || text.trim().length === 0) return;

    // Default to interrupting: two voices talking over each other is never acceptable.
    if (options?.interrupt !== false) this.#cancelQueue();

    await new Promise<void>((resolve) => {
      this.#queue.push({ text, options, settle: resolve });
      void this.#drain();
    });
  }

  stop(): void {
    this.#cancelQueue();
  }

  async replayLast(): Promise<void> {
    if (this.#last === null) return;
    await this.speak(this.#last.text, { ...this.#last.options, interrupt: true });
  }

  #cancelQueue(): void {
    const pending = this.#queue.splice(0, this.#queue.length);
    for (const item of pending) item.settle();
    this.#synthesis?.cancel();
    // Not every backend fires onend after cancel, so settle the in-flight one ourselves.
    this.#finishActive?.();
  }

  async #drain(): Promise<void> {
    if (this.#speaking) return;
    const synthesis = this.#synthesis;
    const createUtterance = this.#createUtterance;
    if (synthesis === null || createUtterance === null) return;

    this.#speaking = true;
    try {
      for (;;) {
        const next = this.#queue.shift();
        if (next === undefined) break;

        await new Promise<void>((resolve) => {
          let settled = false;
          /** The give-up timer for this line. Exactly one is armed at a time; see `arm`. */
          let watchdog: ReturnType<typeof setTimeout> | null = null;
          const finish = (): void => {
            if (settled) return;
            settled = true;
            if (watchdog !== null) clearTimeout(watchdog);
            watchdog = null;
            this.#finishActive = null;
            next.settle();
            resolve();
          };
          this.#finishActive = finish;

          const utterance = createUtterance(next.text);
          utterance.volume = this.#volume;
          const rate = next.options?.speakingRate ?? this.#profile?.speakingRate ?? 1;
          utterance.rate = rate;
          utterance.pitch = next.options?.pitch ?? this.#profile?.pitch ?? 1;
          utterance.onend = finish;
          // A cancelled or failed utterance resolves too: narration is never load-bearing.
          utterance.onerror = finish;

          /**
           * …and an utterance the engine says **nothing** about resolves too (round 4, reshaped by
           * S5-6).
           *
           * `onend` and `onerror` are the only two ways a line can end, and a voiceless or headless
           * browser fires neither: it accepts `speak()` and goes quiet for ever. Without a bound,
           * that single line holds the queue open for the rest of the sitting and every sentence
           * behind it — the next round's instruction, the repeat a child asks for, every hint — is
           * lost.
           *
           * **What the bound measures is engine activity, never utterance length.** Round 4 gave the
           * whole line one length-derived deadline from the moment `speak()` was called, and a live
           * but slow voice ran past it: a five-year-old's instruction was cancelled mid-sentence,
           * which is a worse harm than the stall it repairs. So there are two questions, asked in
           * order, and neither of them is "is this taking a while":
           *
           *  1. **Did it begin?** No `onstart` inside `utteranceStartTimeoutMs` means nobody is
           *     saying this line. Short, and safe to be short, because an utterance that never
           *     started cannot be cut off.
           *  2. **Is it still going?** From the start itself, the rest of the text at the slowest
           *     delivery anybody hears — re-armed at every word boundary the engine reports, so a
           *     line that is visibly progressing keeps being given room. Boundary events are an
           *     extension, never a dependency: engines that fire none still get the whole-line
           *     bound, which no live utterance can reach.
           *
           * Giving up does three things in the order they are needed: tell the engine to drop the
           * line, settle the caller, and let the queue move on. `finish` is idempotent by
           * construction (`settled`), so a late `onend` after a watchdog is harmless.
           */
          const giveUp = (): void => {
            // Take the line back before moving on, so a backend that wakes up later cannot start
            // speaking over the next one.
            try {
              synthesis.cancel();
            } catch {
              // A cancel that throws is the engine's problem, not a child's: keep going.
            }
            finish();
          };
          /**
           * Arms the single give-up timer. `whenDue` may decline once — returning `false` means it
           * has re-armed and this deadline was not the last word.
           */
          const arm = (afterMs: number, whenDue?: () => boolean): void => {
            if (settled) return;
            if (watchdog !== null) clearTimeout(watchdog);
            watchdog = setTimeout(() => {
              if (whenDue !== undefined && !whenDue()) return;
              giveUp();
            }, afterMs);
          };

          /**
           * Progress is a `charIndex` that has **moved** (S6-6).
           *
           * Re-arming on *a callback* rather than on *progress* is how a stalled engine wedges the
           * queue for ever: one that repeats the same boundary every second restores the full
           * remaining-text bound every time, and the remaining text never shrinks. A boundary that
           * has not advanced is not an engine speaking, it is an engine talking to itself.
           */
          let spokenThrough = -1;
          let started = false;

          utterance.onstart = () => {
            started = true;
            arm(remainingSpeechMs(next.text, 0, rate));
          };
          utterance.onboundary = (event: SpeechSynthesisEvent) => {
            if (event.charIndex <= spokenThrough) return;
            spokenThrough = event.charIndex;
            arm(remainingSpeechMs(next.text, spokenThrough, rate));
          };

          /**
           * …and a missing `onstart` decides nothing on its own (S6-6).
           *
           * `onstart` is optional in practice: engines and voices exist that accept `speak()`, talk
           * for several seconds and announce nothing until `onend`. Round 5 read three seconds of
           * silence as proof that nothing had begun and cancelled — truncating a live instruction,
           * which is the one harm this watchdog exists to prevent and the very thing S5-6 closed
           * from the other direction.
           *
           * So the start bound is no longer a verdict; it is the moment we stop *expecting* to be
           * told. If nothing has been announced by then, the line is given the conservative bound it
           * would have had anyway — the whole text at the slowest delivery anybody hears — before
           * the queue moves on. A genuinely dead engine therefore waits a few seconds longer than it
           * did, which is exactly the direction the round-4 derivation chose: too long means one
           * dropped line stays dropped a little longer, too short means cutting a five-year-old off
           * mid-sentence, and only the second is a harm.
           */
          arm(NARRATION_BOUNDS.utteranceStartTimeoutMs, () => {
            if (started) return true;
            arm(remainingSpeechMs(next.text, 0, rate));
            return false;
          });

          try {
            synthesis.speak(utterance);
          } catch {
            finish();
          }
        });
      }
    } finally {
      this.#speaking = false;
    }
  }
}

/** Convenience factory matching the other platform services. */
export function createBrowserTtsNarrationService(
  options: BrowserTtsNarrationServiceOptions = {},
): BrowserTtsNarrationService {
  return new BrowserTtsNarrationService(options);
}
